import { useMemo } from "react";
import {
    isAssetCounterpartType,
    isEndpointCounterpartType,
    mapAssetToCounterpart,
    mapEndpointToCounterpart,
} from "../helpers/flowForm";
import useDataFlowAssets from "./useDataFlowAssets";
import useDataFlowEndpoints from "./useDataFlowEndpoints";
import useDebouncedValue from "./useDebouncedValue";

const useCounterpartItems = ({
    type,
    search = "",
    wrapperAssetId,
    enabled = false,
    t,
} = {}) => {
    const debouncedSearch = useDebouncedValue(search);
    const isAssetType = isAssetCounterpartType(type);
    const isEndpointType = isEndpointCounterpartType(type);

    const assetsQuery = useDataFlowAssets({
        type,
        enabled: enabled && isAssetType,
        t,
    });
    const endpointsQuery = useDataFlowEndpoints({
        type,
        q: debouncedSearch,
        enabled: enabled && isEndpointType,
        t,
    });

    const items = useMemo(() => {
        if (isEndpointType) {
            return (endpointsQuery.data || []).map(mapEndpointToCounterpart);
        }

        if (!isAssetType) {
            return [];
        }

        const query = String(search || "").trim().toLowerCase();

        return (assetsQuery.data || [])
            .filter((asset) => Number(asset.id) !== Number(wrapperAssetId))
            .filter((asset) =>
                query
                    ? String(asset.name || "")
                          .toLowerCase()
                          .includes(query)
                    : true
            )
            .map((asset) => mapAssetToCounterpart(asset, type));
    }, [
        assetsQuery.data,
        endpointsQuery.data,
        isAssetType,
        isEndpointType,
        search,
        type,
        wrapperAssetId,
    ]);

    return {
        items,
        isLoading:
            (isAssetType && assetsQuery.isLoading) ||
            (isEndpointType && endpointsQuery.isLoading),
    };
};

export default useCounterpartItems;
